import { useEffect, useState } from 'react';
import { Command, CommandEmpty, CommandList } from 'components/ui/command';
import { useGetArchivedChats } from 'pages/hooks/use-archive';
import { Loader2, MessageSquare } from 'lucide-react';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { SidebarItem } from 'components/blocks/layout/types/sidebar-conversation';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { useHandleArchiveUnarchive } from 'pages/services/archive-service';
import { useConfig } from 'hooks/use-config';

/**
 * Archive Tab Component
 * 
 * A settings tab component that displays archived chats and allows users to unarchive them.
 * This tab is used within the SettingsDialog to manage archived conversations.
 * 
 * Features:
 * - List of all archived conversations
 * - Unarchive functionality
 * - Loading states
 * - Empty state when no archives
 * - Integration with the existing settings dialog structure
 * - Responsive design that works on both desktop and mobile
 * 
 * @param tabId - The ID of the current tab (used for consistency with other tabs)
 * @returns An archive management tab component
 */

interface ArchiveTabProps {
    tabId: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ArchiveTab: React.FC<ArchiveTabProps> = ({ tabId }) => {
    const { t } = useTranslation();
    const { isPending: isFetchingArchivedChats, mutateAsync: fetchArchivedChats } = useGetArchivedChats();
    const fetchAndUpdateProjectAllConversations = useChatStore((state) => state.fetchAndUpdateProjectAllConversations);
    const [archivedList, setArchivedList] = useState<SidebarItem[]>([]);
    const { mutateAsync: handleArchiveUnarchive } = useHandleArchiveUnarchive();
    const { configurationSettings } = useConfig();

    useEffect(() => {
        fetchArchivedChats({ page: 0, pageSize: 200 })
            .then((data) => {
                setArchivedList(data.results || []);
            })
            .catch((error) => {
                console.error(error);
            });
    }, [fetchArchivedChats]);

    const handleUnarchive = async (chat: SidebarItem) => {
        try {

            const conversationId = chat.ConversationId;
            await handleArchiveUnarchive({ isArchived: false, conversationId: conversationId });
            if (chat.ProjectId) {
                // appendProjectItemToSidebarProjectsList(chat as ProjectItem, chat.ProjectId);
                // queryClient.invalidateQueries({ queryKey: ["getProject", chat.ProjectId] });
                await fetchAndUpdateProjectAllConversations(configurationSettings, chat.ProjectId);
            } else {
                useChatStore.getInitialState().appendToSidebarConversationsList(chat);
            }
            setArchivedList((prev) => prev.filter((item) => item.ConversationId !== chat.ConversationId));
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className="h-full overflow-auto bg-background">
            <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 max-w-4xl">
                <div className="space-y-6 sm:space-y-8">
                    <div>
                        <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-1">
                            {t('ARCHIVED_CHATS')}
                        </h2>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                            {t('MANAGE_YOUR_ARCHIVED_CONVERSATIONS') || 'Manage your archived conversations'}
                        </p>
                    </div>

                    <Command className="rounded-lg border bg-card">
                        <CommandList className="max-h-[400px] sm:max-h-[500px] overflow-auto relative">
                            {isFetchingArchivedChats ? (
                                <div className="flex items-center justify-center py-8 sm:py-12">
                                    <div className="flex flex-col items-center gap-3 sm:gap-4">
                                        <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 animate-spin text-muted-foreground" />
                                        <span className="text-xs sm:text-sm text-muted-foreground">{t('LOADING_ARCHIVED_CHATS')}...</span>
                                    </div>
                                </div>
                            ) : archivedList.length === 0 ? (
                                <div className="flex items-center justify-center py-8 sm:py-12">
                                    <CommandEmpty className="text-center text-xs sm:text-sm">
                                        {t('NO_ARCHIVED_CONVERSATIONS_FOUND')}
                                    </CommandEmpty>
                                </div>
                            ) : (
                                <table className="w-full">
                                    <thead className="sticky top-0 bg-muted/50 backdrop-blur-sm z-10">
                                        <tr className="border-b">
                                            <th className="text-left p-2 sm:p-4 font-semibold text-xs sm:text-sm">{t('CONVERSATION')}</th>
                                            <th className="text-right p-2 sm:p-4 font-semibold text-xs sm:text-sm">{t('ACTIONS')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {archivedList.map((chat) => (
                                            <tr key={chat.ConversationId} className="border-b hover:bg-accent/50 transition-colors">
                                                <td className="p-2 sm:p-4">
                                                    <div className="flex items-center gap-2 sm:gap-3">
                                                        <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                                                        <span className="font-medium text-xs sm:text-sm text-foreground truncate">{chat.ConversationTitle}</span>
                                                    </div>
                                                </td>
                                                <td className="p-2 sm:p-4 text-right">
                                                    <Button
                                                        onClick={() => handleUnarchive(chat)}
                                                        variant="default"
                                                    >
                                                        {t('UNARCHIVE')}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CommandList>
                    </Command>
                </div>
            </div>
        </div>
    );
};

